/**
 * The translation WebSocket protocol — architecture §8.1.
 *
 * This file is the contract between the browser and the translation server, and
 * it is deliberately the only place that shape is written down on this side.
 * `server/translation/app/protocol.py` mirrors it; the two live in one
 * repository so that a change to either is one commit and they cannot drift.
 *
 * The browser recognises nothing. It opens a socket, declares what it wants,
 * ships PCM, and receives text. That is what makes the feature identical in
 * desktop Chrome and in the Android WebView — `AudioContext` and `WebSocket` are
 * the only platform APIs involved, and neither side depends on a speech API the
 * other might not have (§8.1).
 *
 *   client ──► 'start'    JSON, once, before any audio
 *   client ──► <binary>   PCM16 frames, continuously
 *   client ──► 'stop'     JSON, once, to end cleanly
 *
 *   server ──► 'ready'    JSON, acknowledges 'start' and declares capabilities
 *   server ──► 'segment'  JSON, zero or more
 *   server ──► <binary>   TPCM frames of translated voice, only when 'start'
 *                         asked for `output: 'voice'` (§8.9)
 *   server ──► 'error'    JSON, terminal for the session
 */

/** Sample rate the server expects. 16 kHz mono is what every STT model wants. */
export const TRANSLATION_SAMPLE_RATE = 16_000;

/**
 * How the engine produces segments (§8.5).
 *
 * Batch and streaming are not two pipelines — they are the same one with a
 * different trigger, and the client handles both without branching. The mode is
 * carried so the *UI* can be honest: a batch engine will never revise a caption,
 * so nothing should render as though a revision is still coming.
 */
export type EngineMode = 'batch' | 'streaming';

export interface ProtocolLanguage {
  /** BCP-47, e.g. 'hi-IN'. */
  code: string;
  /** Human-readable, in that language where possible. */
  label: string;
}

/**
 * What the deployed engine can actually do.
 *
 * Sent by the server rather than configured in the client, because whether a
 * language works is a fact about the model, not about the client build. A picker
 * offering a language the engine cannot handle is the same failure as a control
 * that is always visible and never works (§8.3).
 */
export interface ProtocolCapabilities {
  engine: string;
  mode: EngineMode;
  sourceLanguages: readonly ProtocolLanguage[];
  /**
   * May be narrower than `sourceLanguages` — Whisper's translate task is
   * English-target only, so an engine can recognise many languages while being
   * able to translate into just one.
   */
  targetLanguages: readonly ProtocolLanguage[];
  emitsPartials: boolean;
  /** True for a voice engine, which also sends TPCM frames (§8.9). */
  emitsAudio: boolean;
}

/** What a session returns: captions only, or translated voice as well. */
export type OutputKind = 'text' | 'voice';

/** Opens a session. Sent once, before any audio. */
export interface StartMessage {
  type: 'start';
  /** The language this side speaks — what the recogniser should expect. */
  sourceLanguage: string;
  /** The far side's `listensIn` — what its captions should be in (§8.2). */
  targetLanguage: string;
  sampleRate: number;
  /**
   * Correlates this socket with the consultation in the logs (§9.1), so one
   * session is a single queryable timeline across web, native and server.
   */
  sessionId: string;
  /**
   * Validated against the existing API. The socket carries patient speech and
   * cannot be open; the field is present from the first version so that adding
   * auth later is not a protocol change (§8.8).
   */
  token?: string;
  /** Absent means `'text'`, so the caption path is unchanged. */
  output?: OutputKind;
}

/** Ends a session cleanly, so the server can flush a partial utterance. */
export interface StopMessage {
  type: 'stop';
}

export type ClientMessage = StartMessage | StopMessage;

/** Acknowledges `start`. No audio should be sent before this arrives. */
export interface ReadyMessage {
  type: 'ready';
  capabilities: ProtocolCapabilities;
}

/**
 * A recognised span.
 *
 * Mirrors `TranslationSegment` field for field, so the provider that receives
 * this is a thin adapter with no translation logic of its own.
 *
 * On a partial, `translatedText` is empty and `sourceText` carries what has been
 * heard so far: machine translation of half a sentence is frequently wrong, and
 * wrong *fluently*, which reads as fact rather than as a gap. The speaker sees
 * their own words appear live; the listener waits for the sentence to finish
 * (§8.5).
 */
export interface SegmentMessage {
  type: 'segment';
  /** Stable across revisions of the same span, so a caption updates in place. */
  id: string;
  sourceText: string;
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
  final: boolean;
}

/**
 * Terminal for the session.
 *
 * Translation is strictly additive, so this disables captions with a visible
 * notice and leaves the consultation untouched (§8).
 */
export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

export type ServerMessage = ReadyMessage | SegmentMessage | ErrorMessage;

export type DecodeResult<T> =
  | { ok: true; message: T }
  | { ok: false; reason: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function decodeLanguages(value: unknown): readonly ProtocolLanguage[] | null {
  if (!Array.isArray(value)) return null;

  const languages: ProtocolLanguage[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) return null;
    if (!isString(entry['code']) || !isString(entry['label'])) return null;
    languages.push({ code: entry['code'], label: entry['label'] });
  }

  return languages;
}

function decodeCapabilities(value: unknown): ProtocolCapabilities | null {
  if (!isRecord(value)) return null;

  const mode = value['mode'];
  if (mode !== 'batch' && mode !== 'streaming') return null;

  const sourceLanguages = decodeLanguages(value['sourceLanguages']);
  const targetLanguages = decodeLanguages(value['targetLanguages']);
  if (!sourceLanguages || !targetLanguages) return null;

  if (!isString(value['engine'])) return null;
  if (typeof value['emitsPartials'] !== 'boolean') return null;
  if (typeof value['emitsAudio'] !== 'boolean') return null;

  return {
    engine: value['engine'],
    mode,
    sourceLanguages,
    targetLanguages,
    emitsPartials: value['emitsPartials'],
    emitsAudio: value['emitsAudio'],
  };
}

/**
 * Parse a message from the server.
 *
 * Rejects loudly rather than coercing, for the same reason the control plane
 * does (§6.1): a malformed message patched up with defaults becomes a caption
 * nobody said. On this channel that would put words into a clinician's mouth.
 */
export function decodeServerMessage(raw: string): DecodeResult<ServerMessage> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'not valid JSON' };
  }

  if (!isRecord(parsed)) {
    return { ok: false, reason: 'not an object' };
  }

  const type = parsed['type'];

  if (type === 'ready') {
    const capabilities = decodeCapabilities(parsed['capabilities']);
    if (!capabilities) {
      return { ok: false, reason: 'ready message has malformed capabilities' };
    }
    return { ok: true, message: { type: 'ready', capabilities } };
  }

  if (type === 'segment') {
    for (const field of [
      'id',
      'sourceText',
      'translatedText',
      'sourceLanguage',
      'targetLanguage',
    ]) {
      if (!isString(parsed[field])) {
        return { ok: false, reason: `segment is missing string '${field}'` };
      }
    }
    if (typeof parsed['final'] !== 'boolean') {
      return { ok: false, reason: "segment is missing boolean 'final'" };
    }

    return {
      ok: true,
      message: {
        type: 'segment',
        id: parsed['id'] as string,
        sourceText: parsed['sourceText'] as string,
        translatedText: parsed['translatedText'] as string,
        sourceLanguage: parsed['sourceLanguage'] as string,
        targetLanguage: parsed['targetLanguage'] as string,
        final: parsed['final'],
      },
    };
  }

  if (type === 'error') {
    if (!isString(parsed['code']) || !isString(parsed['message'])) {
      return { ok: false, reason: 'error message is malformed' };
    }
    return {
      ok: true,
      message: {
        type: 'error',
        code: parsed['code'],
        message: parsed['message'],
      },
    };
  }

  return { ok: false, reason: `unknown message type: ${String(type)}` };
}
